/**
 * Lazy Analytics Loader
 * Carga scripts tras interacción o idle (mejor FCP/LCP).
 * No corre en /admin ni shell admin — evita GA/Metricool compitiendo con APIs admin.
 *
 * Consent: GA + Metricool only after cookie Accept. Ahrefs is cookieless (public).
 * Tally is form widgets (loads only when needed / after interaction on public).
 */

import { initAhrefsAnalytics } from "@/lib/ahrefsAnalytics";
import { hasAcceptedAnalyticsCookies } from "@/lib/cookieConsentStorage";
import { GA_MEASUREMENT_ID } from "@/lib/gaConfig";
import { isAdminShellPath } from "@/lib/geoConsentRoutes";
const METRICOOL_HASH_FROM_ENV = (
  import.meta.env.VITE_METRICOOL_HASH as string | undefined
)?.trim();

/** Same hash as the c3po.jpg pixel in index.html (injected at build from env or default). */
function resolveMetricoolHash(): string | null {
  if (METRICOOL_HASH_FROM_ENV) return METRICOOL_HASH_FROM_ENV;
  const pixel = document.querySelector<HTMLImageElement>(
    'img[src*="tracker.metricool.com/c3po.jpg"]',
  );
  const fromPixel = pixel?.src.match(/[?&]hash=([^&]+)/)?.[1]?.trim();
  return fromPixel || "d07265c6f2fbf27c697a89bb88215299";
}

let analyticsLoaded = false;
let consentListenersBound = false;

function loadGoogleAnalytics(): void {
  if (!GA_MEASUREMENT_ID) return;
  if (document.querySelector('script[src*="googletagmanager.com/gtag"]')) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer.push(args);
  };

  window.gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    functionality_storage: "granted",
    personalization_storage: "denied",
    security_storage: "granted",
    wait_for_update: 2000,
  });

  const cookieConsent =
    typeof localStorage !== "undefined"
      ? localStorage.getItem("cookie_consent")
      : null;
  if (cookieConsent === "accepted") {
    window.gtag("consent", "update", {
      analytics_storage: "granted",
      ad_storage: "granted",
      ad_user_data: "granted",
      ad_personalization: "granted",
      functionality_storage: "granted",
      personalization_storage: "granted",
      security_storage: "granted",
    });
  } else if (cookieConsent === "declined") {
    window.gtag("consent", "update", {
      analytics_storage: "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      functionality_storage: "granted",
      personalization_storage: "denied",
      security_storage: "granted",
    });
  }

  const script = document.createElement("script");
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  script.async = true;
  script.onload = () => {
    window.gtag("js", new Date());
    window.gtag("config", GA_MEASUREMENT_ID, {
      anonymize_ip: true,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });
  };
  document.head.appendChild(script);
}

function loadMetricool(): void {
  const hash = resolveMetricoolHash();
  if (!hash) return;
  if (document.querySelector('script[src*="metricool.com"]')) return;

  const script = document.createElement("script");
  script.src = "https://tracker.metricool.com/resources/be.js";
  script.async = true;
  script.onload = () => {
    if (typeof window.beTracker !== "undefined") {
      window.beTracker.t({ hash });
    }
  };
  document.head.appendChild(script);
}

function loadTally(): void {
  if (document.querySelector('script[src*="tally.so"]')) return;

  const script = document.createElement("script");
  script.src = "https://tally.so/widgets/embed.js";
  script.async = true;
  document.head.appendChild(script);
}

/**
 * Load consent-gated analytics (GA + Metricool) + form widgets + Ahrefs ensure.
 * Safe to call multiple times; only loads GA/Metricool after Accept.
 */
export function loadAllAnalytics(): void {
  // Ahrefs: cookieless public measurement — always ok on public site
  initAhrefsAnalytics();

  if (!hasAcceptedAnalyticsCookies()) {
    return;
  }

  if (analyticsLoaded) return;
  analyticsLoaded = true;

  loadGoogleAnalytics();
  loadMetricool();
  loadTally();
}

/** Call after cookie Accept so GA/Metricool load without waiting for idle. */
export function loadAnalyticsAfterConsentAccepted(): void {
  analyticsLoaded = false;
  loadAllAnalytics();
}

export function initAnalyticsLoader(): void {
  if (typeof window !== "undefined" && isAdminShellPath(window.location.pathname)) {
    // Allow PostHog load for dual-cta admin tools
    const p = window.location.pathname;
    if (!p.startsWith("/admin/dual-cta-home") && !p.startsWith("/admin/dual-cta-templates") && !p.startsWith("/admin/video-ab-slots")) {
      return;
    }
  }

  // Re-load optional analytics when user accepts later in the session
  if (!consentListenersBound && typeof window !== "undefined") {
    consentListenersBound = true;
    window.addEventListener("cookie-consent-changed", ((e: CustomEvent<{ accepted?: boolean }>) => {
      if (e.detail?.accepted) loadAnalyticsAfterConsentAccepted();
    }) as EventListener);
  }

  // No GA until Accept (opt-in). Idle/interaction only loads if already accepted.
  const events = ["click", "scroll", "keydown", "touchstart"];

  const loadOnInteraction = () => {
    loadAllAnalytics();
    events.forEach((event) => {
      window.removeEventListener(event, loadOnInteraction, { capture: true });
    });
  };

  events.forEach((event) => {
    window.addEventListener(event, loadOnInteraction, {
      capture: true,
      passive: true,
    });
  });

  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(() => loadAllAnalytics(), { timeout: 4000 });
  } else {
    setTimeout(loadAllAnalytics, 4000);
  }
}

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    beTracker: {
      t: (config: { hash: string }) => void;
    };
  }
}